import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { BenefitsPreviewPanel } from './BenefitsPreviewPanel';

interface PreviewSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PreviewSheet({ isOpen, onClose }: PreviewSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 z-50 bg-black/60 transition-opacity duration-300 lg:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={clsx(
          'fixed inset-x-0 bottom-0 z-50 bg-background-card rounded-t-2xl transition-transform duration-300 ease-out lg:hidden',
          'max-h-[85vh] flex flex-col',
          isOpen ? 'translate-y-0' : 'translate-y-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Character preview"
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-1 rounded-full bg-text-muted/30 hover:bg-text-muted/50 transition-colors"
            aria-label="Close preview"
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-border-default">
          <h2 className="text-lg font-semibold text-text-primary">Character Preview</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 text-text-muted hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <BenefitsPreviewPanel isFullPanel showExportButtons />
        </div>
      </div>
    </>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
